import type { PayloadAction } from "@reduxjs/toolkit";
import { createSlice } from "@reduxjs/toolkit";

import type { AIContextKeyType } from "components/ai/hooks/use-ai-context";
import type { ToolNameType } from "components/ai/tools/types";
import {
  AIContextPartsResponse,
  AIModelType,
  AIServerFileType,
  AIServerRoleType,
} from "components/server-types";
import { createReplaceAllReducer } from "store/utils";

/**
 * Changing this key (e.g. by bumping the version) will clear everyone's
 * AI-related state on next refresh / deploy.
 * Since AI messages aren't critical, we can use this to reset state
 * when we make changes to the implementation that would otherwise require
 * a more complex migration.
 */
export const LOCAL_AI_STATE_KEY = "monarch-local-ai-v3";

export type AIMessageBase = {
  id: string;
  role: AIServerRoleType;
  text: string;
  createdAt: string; // ISO datetime string
  finishedAt?: string; // ISO datetime string
  isLoading?: boolean;
};

export type AIContextItem = {
  id: AIContextKeyType;
  text: string;
  type: "context";
  role: AIServerRoleType;
};

export type AITextMessage = AIMessageBase & {
  type: "message";
  files?: AIServerFileType[];
};

// TODO: Maybe this shouldn't inherit "text" from the base message type
export type AIToolMessage = AIMessageBase & {
  type: "function_call";
  name: ToolNameType;
  arguments: Record<string, unknown>;
  call_id: string;
  status?: string;
};

export type AIToolResponseMessage = Omit<AIMessageBase, "text"> & {
  type: "function_call_output";
  call_id: string;
  output: unknown;
  error?: string;
};

export type AIChatMessage = AITextMessage | AIToolMessage | AIToolResponseMessage;

/** Worksheet / attachment file shape (e.g. ``doc-query``); not persisted on the AI slice. */
export type AIFileType = AIServerFileType & {
  source?: string;
  error?: string;
};

/** One recorded AI assessment feedback text while "record feedback style" is on (MP admin). */
export type FeedbackStyleSample = {
  submissionId: string;
  feedback: string;
  recordedAt: string;
};

export type AIState = {
  aiChatMessages: Record<string, AIChatMessage>;
  /**
   * Context items sent alongside chat history. Stored separately from chat messages
   * and indexed by a stable id provided by callers of setAiChatData.
   */
  contextItems: Partial<Record<AIContextKeyType, AIContextItem>>;
  /**
   * Last system context returned by the server with a chat or schema response (for debugger).
   */
  lastAiContextParts: AIContextPartsResponse | null;
  /**
   * Tools currently available to the AI.
   * Actual tool definitions are in src/components/ai/tools,
   * these are just references.
   */
  tools: ToolNameType[];

  /* whether on not we allow AI auto-assess submissions */
  assessWithAIMode: boolean;
  /**
   * When true, each saved manual assessment comment is stored in `assessmentFeedbackStyleSamples` for
   * inferring a voice/style (MP admin flow). Stays on until turned off.
   */
  isRecordingFeedbackStyle: boolean;
  /** Collected while `isRecordingFeedbackStyle` is true; used when deriving style. */
  assessmentFeedbackStyleSamples: FeedbackStyleSample[];
  /** True while POST derive-assessment-feedback-style is in flight; not persisted. */
  isDerivingFeedbackStyle: boolean;
  /* whether or not we allow ai to see student responses when viewing page content on chat panel */
  isStudentResponsesForAIEnabled: boolean;
  /** Doc view access id while PageWorksheet is mounted; sent with chat for server worksheet context. */
  worksheetChatDocViewAccessId: string | null;
  aiDebuggerVisible: boolean;
  userSelectedAIModel: AIModelType | null;
};

const defaultState: AIState = {
  aiChatMessages: {},
  contextItems: {},
  lastAiContextParts: null,
  tools: [],
  assessWithAIMode: false,
  isRecordingFeedbackStyle: false,
  assessmentFeedbackStyleSamples: [],
  isDerivingFeedbackStyle: false,
  isStudentResponsesForAIEnabled: false,
  worksheetChatDocViewAccessId: null,
  aiDebuggerVisible: false,
  userSelectedAIModel: null,
};

const retrieveLocalAiState = () => {
  const data = localStorage.getItem(LOCAL_AI_STATE_KEY);
  if (!data) return defaultState;
  const storedState = JSON.parse(data) as Partial<AIState>;

  return {
    ...defaultState,
    ...storedState,
    // Not persisted (managed by hooks / request-scoped)
    contextItems: {},
    tools: [],
    worksheetChatDocViewAccessId: null,
    isDerivingFeedbackStyle: false,
  };
};

const saveLocalState = (state: AIState) => {
  localStorage.setItem(LOCAL_AI_STATE_KEY, JSON.stringify(state));
};

const deleteLocalState = () => {
  localStorage.removeItem(LOCAL_AI_STATE_KEY);
};

const initialState = retrieveLocalAiState;

export const AIStore = createSlice({
  name: "ai",
  initialState,
  reducers: {
    setAssessWithAIMode: (state, action: PayloadAction<boolean>) => {
      state.assessWithAIMode = action.payload;
      saveLocalState(state);
    },
    setIsRecordingFeedbackStyle: (state, action: PayloadAction<boolean>) => {
      const enabled = action.payload;
      state.isRecordingFeedbackStyle = enabled;
      if (enabled) {
        state.assessmentFeedbackStyleSamples = [];
      }
      saveLocalState(state);
    },
    addFeedbackStyleSample: (
      state,
      action: PayloadAction<Pick<FeedbackStyleSample, "submissionId" | "feedback">>,
    ) => {
      if (!state.assessmentFeedbackStyleSamples) {
        state.assessmentFeedbackStyleSamples = [];
      }
      const { submissionId, feedback } = action.payload;
      const at = new Date().toISOString();
      const idx = state.assessmentFeedbackStyleSamples.findIndex(
        (r) => r.submissionId === submissionId,
      );
      if (idx >= 0) {
        state.assessmentFeedbackStyleSamples[idx] = { submissionId, feedback, recordedAt: at };
      } else {
        state.assessmentFeedbackStyleSamples.push({ submissionId, feedback, recordedAt: at });
      }
      saveLocalState(state);
    },
    clearRecordedFeedbackStyle: (state) => {
      state.assessmentFeedbackStyleSamples = [];
      saveLocalState(state);
    },
    setIsDerivingFeedbackStyle: (state, action: PayloadAction<boolean>) => {
      state.isDerivingFeedbackStyle = action.payload;
    },
    addTool: (state, action: PayloadAction<ToolNameType>) => {
      if (!state.tools.includes(action.payload)) {
        state.tools.push(action.payload);
        saveLocalState(state);
      }
    },
    removeTool: (state, action: PayloadAction<ToolNameType>) => {
      state.tools = state.tools.filter((tool) => tool !== action.payload);
      saveLocalState(state);
    },
    setAiChatData: (state, action: PayloadAction<AIContextItem>) => {
      if (!state.contextItems) state.contextItems = {};
      state.contextItems[action.payload.id] = { ...action.payload };
    },
    removeAiChatData: (state, action: PayloadAction<AIContextKeyType>) => {
      if (state.contextItems) {
        delete state.contextItems[action.payload];
      }
    },
    setAiChatMessage: (state, action: PayloadAction<AIChatMessage>) => {
      if (!state.aiChatMessages) state.aiChatMessages = {};
      state.aiChatMessages[action.payload.id] = { ...action.payload };
      // Uncomment for debugging:
      // console.info(`setAiChatMessage id=${action.payload.id}:`, state.aiChatMessages[action.payload.id]);
      saveLocalState(state);
    },
    deleteAiChatMessage: (state, action: PayloadAction<string>) => {
      if (!state.aiChatMessages) state.aiChatMessages = {};
      delete state.aiChatMessages[action.payload];
      saveLocalState(state);
    },
    clearAiChatMessages: (state) => {
      state.aiChatMessages = {};
      saveLocalState(state);
    },
    setStudentResponsesForAIEnabled: (state, action: PayloadAction<boolean>) => {
      state.isStudentResponsesForAIEnabled = action.payload;
      saveLocalState(state);
    },
    setWorksheetChatDocViewAccessId: (state, action: PayloadAction<string | null>) => {
      state.worksheetChatDocViewAccessId = action.payload;
    },
    setAiDebuggerVisible: (state, action: PayloadAction<boolean>) => {
      state.aiDebuggerVisible = action.payload;
      saveLocalState(state);
    },
    setUserSelectedAIModel: (state, action: PayloadAction<AIModelType | null>) => {
      state.userSelectedAIModel = action.payload;
      saveLocalState(state);
    },
    setLastAiContextParts: (state, action: PayloadAction<AIContextPartsResponse | null>) => {
      state.lastAiContextParts = action.payload;
      saveLocalState(state);
    },
  },
  extraReducers: createReplaceAllReducer<AIState>("ai", initialState, () => {
    deleteLocalState();
  }),
});

export const aiStateActions = AIStore.actions;
